import React, { useEffect, useRef, useState } from "react";

import { concatClassNames } from "@thelabnyc/thelabui/src/utils/styles";

import styles from "./ScrollProgress.module.scss";

interface ScrollProgressProps {
    scrollRef: React.RefObject<HTMLDivElement | null>;
    className?: string;
}

function calculateProgress(element: HTMLDivElement): number {
    if (element.scrollWidth === 0) return 0;
    return (
        ((element.scrollLeft + element.clientWidth) / element.scrollWidth) * 100
    );
}

/**
 * A scroll progress bar that tracks horizontal scroll position.
 * Pair with a Carousel's `scrollContainerProps.ref` and `onScroll`.
 */
export function ScrollProgress({ scrollRef, className }: ScrollProgressProps) {
    const [progress, setProgress] = useState(0);
    const rafRef = useRef(0);

    useEffect(() => {
        const element = scrollRef.current;
        if (!element) return;

        setProgress(calculateProgress(element));

        const onScroll = () => {
            cancelAnimationFrame(rafRef.current);
            rafRef.current = requestAnimationFrame(() => {
                setProgress(calculateProgress(element));
            });
        };

        element.addEventListener("scroll", onScroll, { passive: true });
        return () => {
            element.removeEventListener("scroll", onScroll);
            cancelAnimationFrame(rafRef.current);
        };
    }, []);

    return (
        <div
            className={concatClassNames([styles.progressBar, className])}
            style={{ "--scroll-progress-percent": `${progress}%` }}
            role="presentation"
            aria-hidden="true"
        />
    );
}
